"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";

const COMPASS_AMBER = "#B5680A";
const TIMELINE_ORANGE = "#D4790B";

const cardStyles = [
  {
    // Past
    bg: "#FFFFFF",
    border: "1px solid #EEE9F5",
    borderStyle: "solid",
    labelColor: "#8A88A3",
    titleColor: "#14122B",
    textColor: "#6B6A80",
  },
  {
    // Present
    bg: "#14122B",
    border: "1px solid #14122B",
    borderStyle: "solid",
    labelColor: "rgba(255,255,255,0.7)",
    titleColor: "#FFFFFF",
    textColor: "rgba(255,255,255,0.8)",
  },
  {
    // Forecast
    bg: "#FDF4E3",
    border: "1px dashed #E8C88F",
    borderStyle: "dashed",
    labelColor: COMPASS_AMBER,
    titleColor: "#14122B",
    textColor: "#6B6A80",
  },
];

const CompassTimelineSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const raw = t("compassTimeline.cards", { returnObjects: true });
  const cards = Array.isArray(raw) ? raw : [];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#FFFFFF",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 700,
              color: COMPASS_AMBER,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("compassTimeline.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "24px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("compassTimeline.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "15px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontWeight: 400,
              fontFamily: "var(--font-outfit)",
              maxWidth: 730,
              mx: "auto",
            }}
          >
            {t("compassTimeline.description")}
          </Typography>
        </Box>

        {/* Timeline line */}
        <Box
          aria-hidden
          sx={{
            display: "flex",
            alignItems: "center",
            mb: { xs: 3, md: 4 },
          }}
        >
          {/* Solid part (past) */}
          <Box
            sx={{
              flex: 2,
              height: 0,
              borderTop: "3px solid #14122B",
            }}
          />
          {/* Dot (present) */}
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: TIMELINE_ORANGE,
              boxShadow: "0 0 0 5px rgba(232,133,12,0.22)",
              flexShrink: 0,
            }}
          />
          {/* Dashed part (forecast) */}
          <Box
            sx={{
              flex: 2,
              height: 0,
              borderTop: `3px dashed ${TIMELINE_ORANGE}`,
            }}
          />
        </Box>

        {/* Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 2, md: 2.5 },
            mb: { xs: 4, md: 6 },
          }}
        >
          {cards.map((card, index) => {
            const s = cardStyles[index] || cardStyles[0];

            return (
              <Box
                key={index}
                sx={{
                  backgroundColor: s.bg,
                  border: s.border,
                  borderRadius: "14px",
                  px: { xs: 2.5, md: 2.75 },
                  py: { xs: 2.5, md: 3 },
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    color: s.labelColor,
                    fontFamily: "var(--font-outfit)",
                    mb: 1,
                  }}
                >
                  {card.label}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "17px", md: "19px" },
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: s.titleColor,
                    fontFamily: "var(--font-plusjakarta)",
                    mb: 1,
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "12px", md: "12.5px" },
                    color: s.textColor,
                    lineHeight: 1.6,
                    fontWeight: 400,
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {card.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* CTA */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <NextLink
            href="/free-trial-of-ai-recruiting-tool"
            passHref
            legacyBehavior
          >
            <Button
              component="a"
              sx={{
                bgcolor: "#000",
                color: "white",
                px: { xs: 3, sm: 3.5 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#B5680A",
                  transform: "scale(1.03)",
                },
              }}
            >
              {t("compassTimeline.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default CompassTimelineSection;